import React, { useEffect, useState } from 'react';
import { AdminOrgUser, AdminOrgSummary } from '../../lib/types';
import {
  adminListOrgUsers, adminChangeUserRole,
  adminDeactivateUser, adminReactivateUser,
  adminChangeMaxUsers, adminConvertTrialToActive, adminExtendTrial,
} from '../../lib/api';
import { Modal, Skeleton, ConfirmModal } from '../ui';

interface Props {
  orgId: string;
  org: AdminOrgSummary;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  onChanged: () => void;
}

function daysSince(iso: string | null): string {
  if (!iso) return 'Never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0)   return 'Today';
  if (days === 1)   return 'Yesterday';
  if (days < 7)     return `${days} days ago`;
  if (days < 30)    return `${Math.floor(days / 7)}w ago`;
  if (days < 365)   return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysRemaining(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

// Not wrapped in React.memo — these are conditional renders (mounted/unmounted with showX flag).
// React.memo on conditional renders provides no performance benefit and can cause stale closure
// issues where onConfirm captures an outdated orgId from a previous org expansion.

function ChangeMaxUsersModal({
  orgName, current, onConfirm, onCancel, busy,
}: { orgName: string; current: number; onConfirm: (n: number) => void; onCancel: () => void; busy: boolean }) {
  const [value, setValue] = useState<number>(current);
  const isValid = Number.isInteger(value) && value >= 1 && value <= 1000;
  return (
    <Modal
      open={true}
      onClose={onCancel}
      title="Edit seats"
      subtitle={orgName}
      size="sm"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={busy || !isValid || value === current} onClick={() => onConfirm(value)}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-2">
        <label className="field-label">Total seats</label>
        <input
          className="input"
          type="number"
          min={1}
          max={1000}
          value={value}
          onChange={e => setValue(parseInt(e.target.value) || 0)}
          autoFocus
        />
        <div className="field-hint">Current usage is enforced by the invite Edge Function — reducing seats below current active users will only prevent new invites, not remove existing ones.</div>
      </div>
    </Modal>
  );
}

function ExtendTrialModal({
  orgName, onConfirm, onCancel, busy,
}: { orgName: string; onConfirm: (days: number) => void; onCancel: () => void; busy: boolean }) {
  const [days, setDays] = useState(7);
  return (
    <Modal
      open={true}
      onClose={onCancel}
      title="Extend trial"
      subtitle={orgName}
      size="sm"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => onConfirm(days)}>
            {busy ? 'Saving…' : `Add ${days} day${days !== 1 ? 's' : ''}`}
          </button>
        </>
      }
    >
      <div>
        <label className="field-label">Add how many days?</label>
        <input className="input" type="number" min={1} max={365} value={days} onChange={e => setDays(Math.max(1, parseInt(e.target.value) || 1))} />
      </div>
    </Modal>
  );
}

export function AdminOrgDetail({ orgId, org, showToast, onChanged }: Props) {
  const [users, setUsers] = useState<AdminOrgUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingDeactivate, setPendingDeactivate] = useState<AdminOrgUser | null>(null);
  const [pendingConvert, setPendingConvert] = useState(false);
  const [showChangeMaxUsers, setShowChangeMaxUsers] = useState(false);
  const [showExtendTrial, setShowExtendTrial] = useState(false);
  const [orgBusy, setOrgBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminListOrgUsers(orgId).then(res => {
      if (!active) return;
      if (res.ok && res.data) setUsers(res.data);
      else showToast(res.message || 'Failed to load users', 'error');
      setLoading(false);
    });
    return () => { active = false; };
  }, [orgId, showToast]);

  const handleRoleChange = async (user: AdminOrgUser, newRole: 'org_admin' | 'dept_admin') => {
    const res = await adminChangeUserRole(user.id, newRole);
    if (res.ok) {
      showToast('Role updated', 'success');
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, role: newRole } : u)));
    } else {
      showToast(res.message || 'Failed to change role', 'error');
    }
  };

  const handleDeactivate = async (user: AdminOrgUser) => {
    const res = await adminDeactivateUser(user.id);
    if (res.ok) {
      showToast('User deactivated', 'success');
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, is_active: false } : u)));
      onChanged();
    } else {
      showToast(res.message || 'Failed to deactivate', 'error');
    }
  };

  const handleReactivate = async (user: AdminOrgUser) => {
    const res = await adminReactivateUser(user.id);
    if (res.ok) {
      showToast('User reactivated', 'success');
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, is_active: true } : u)));
      onChanged();
    } else {
      showToast(res.message || 'Failed to reactivate', 'error');
    }
  };

  const handleChangeMaxUsers = async (max_users: number) => {
    setOrgBusy(true);
    const res = await adminChangeMaxUsers(orgId, max_users);
    setOrgBusy(false);
    setShowChangeMaxUsers(false);
    if (res.ok) { showToast(`Seats updated to ${max_users}`, 'success'); onChanged(); }
    else showToast(res.message || 'Failed to update seats', 'error');
  };

  const handleConvertToActive = async () => {
    setOrgBusy(true);
    const res = await adminConvertTrialToActive(orgId);
    setOrgBusy(false);
    setPendingConvert(false);
    if (res.ok) { showToast('Converted to active subscription', 'success'); onChanged(); }
    else showToast(res.message || 'Failed to convert', 'error');
  };

  const handleExtendTrial = async (days: number) => {
    setOrgBusy(true);
    const res = await adminExtendTrial(orgId, days);
    setOrgBusy(false);
    setShowExtendTrial(false);
    if (res.ok) { showToast(`Trial extended by ${days} day(s)`, 'success'); onChanged(); }
    else showToast(res.message || 'Failed to extend trial', 'error');
  };

  const isTrial = org.subscription_status === 'trial';
  const trialDays = isTrial && org.trial_ends_at ? daysRemaining(org.trial_ends_at) : null;

  const headerCell: React.CSSProperties = {
    padding: 'var(--sp-2) var(--sp-3)',
    fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text2)',
    textAlign: 'left', background: 'var(--surface3)', borderBottom: '1px solid var(--border)',
  };
  const cell: React.CSSProperties = {
    padding: 'var(--sp-3)', fontSize: 'var(--fs-base)', borderBottom: '1px solid var(--border)',
  };

  return (
    <div style={{ padding: 'var(--sp-4) var(--sp-5)' }}>
      {/* Org-level actions */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button className="btn btn-sm btn-secondary" onClick={() => setShowChangeMaxUsers(true)}>
          Edit seats
        </button>
        {isTrial && (
          <>
            <button className="btn btn-sm btn-secondary" onClick={() => setPendingConvert(true)}>
              Convert to active
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => setShowExtendTrial(true)}>
              Extend trial
            </button>
          </>
        )}
        {isTrial && org.trial_ends_at && (
          <span className="text-xs text-muted" style={{ marginLeft: 'auto' }}>
            Trial ends {formatDate(org.trial_ends_at)}
            {trialDays !== null && (
              <span style={{ color: trialDays <= 3 ? 'var(--red)' : trialDays <= 7 ? 'var(--amber)' : 'var(--text3)', marginLeft: 4 }}>
                ({trialDays <= 0 ? 'expired' : `${trialDays}d left`})
              </span>
            )}
          </span>
        )}
      </div>

      <ConfirmModal
        open={!!pendingDeactivate}
        title="Deactivate user"
        message={`Deactivate ${pendingDeactivate?.display_name || pendingDeactivate?.email}? They will lose access immediately but all their data is preserved.`}
        confirmLabel="Deactivate"
        destructive
        onConfirm={() => { const u = pendingDeactivate; setPendingDeactivate(null); if (u) handleDeactivate(u); }}
        onCancel={() => setPendingDeactivate(null)}
      />

      <ConfirmModal
        open={pendingConvert}
        title="Convert trial to active"
        message={`This will end ${org.name}'s trial and mark them as an active customer. Continue?`}
        confirmLabel="Convert to active"
        onConfirm={handleConvertToActive}
        onCancel={() => setPendingConvert(false)}
      />

      {showChangeMaxUsers && (
        <ChangeMaxUsersModal
          orgName={org.name}
          current={org.max_users}
          onConfirm={handleChangeMaxUsers}
          onCancel={() => setShowChangeMaxUsers(false)}
          busy={orgBusy}
        />
      )}

      {showExtendTrial && (
        <ExtendTrialModal
          orgName={org.name}
          onConfirm={handleExtendTrial}
          onCancel={() => setShowExtendTrial(false)}
          busy={orgBusy}
        />
      )}

      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton height={12} width="35%" />
          <Skeleton height={36} />
          <Skeleton height={36} />
        </div>
      ) : users.length === 0 ? (
        <div className="text-sm text-subtle text-center" style={{ padding: 'var(--sp-4) 0' }}>
          No users in this organisation yet.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
          <thead>
            <tr>
              <th style={headerCell}>Name</th>
              <th style={headerCell}>Email</th>
              <th style={headerCell}>Role</th>
              <th style={headerCell}>Dept</th>
              <th style={headerCell}>Last login</th>
              <th style={headerCell}>Status</th>
              <th style={headerCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={cell}>{u.display_name || '—'}</td>
                <td style={{ ...cell, fontSize: 'var(--fs-sm)', color: 'var(--text2)' }}>{u.email || '—'}</td>
                <td style={cell}>
                  {u.role === 'super_admin' ? (
                    <span className="badge badge-accent">SUPER ADMIN</span>
                  ) : (
                    <select className="select" value={u.role} onChange={e => handleRoleChange(u, e.target.value as 'org_admin' | 'dept_admin')} style={{ fontSize: 'var(--fs-sm)', padding: '4px 6px', width: 'auto' }}>
                      <option value="org_admin">Account Owner</option>
                      <option value="dept_admin">Team Member</option>
                    </select>
                  )}
                </td>
                <td style={{ ...cell, fontSize: 'var(--fs-sm)' }}>{u.department_name || '—'}</td>
                <td style={{ ...cell, fontSize: 'var(--fs-sm)', color: 'var(--text3)' }}>{daysSince(u.last_sign_in_at)}</td>
                <td style={cell}>
                  <span className="badge" style={{ background: u.is_active ? 'var(--teal-bg)' : 'var(--surface2)', color: u.is_active ? 'var(--teal)' : 'var(--text3)' }}>
                    {u.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </td>
                <td style={cell}>
                  {u.role !== 'super_admin' && (
                    u.is_active
                      ? <button className="btn btn-sm btn-ghost" onClick={() => setPendingDeactivate(u)} style={{ color: 'var(--red)' }}>Deactivate</button>
                      : <button className="btn btn-sm btn-ghost" onClick={() => handleReactivate(u)} style={{ color: 'var(--teal)' }}>Reactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
